import React, { useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';

const palette = ['#e9c46a', '#2a9d8f', '#f4a261', '#e76f51'];

/**
 * Custom Polygon/Radar Chart Component using SVG
 * Matches the design aesthetic from the reference image
 * Enhanced with interactive hover tooltips and smooth animations
 */
const SkillsRadarChart = ({ series, radar, height = 350 }) => {
  const metrics = Array.isArray(radar?.metrics) ? radar.metrics : [];
  const max = typeof radar?.max === 'number' ? radar.max : 10;
  
  // 🎯 Hover state for interactive tooltips
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const badgeLabel = typeof radar?.label === 'string' && radar.label.trim()
    ? radar.label.trim()
    : 'Interview Results';
  const VIEWBOX_WIDTH = 420;
  const VIEWBOX_HEIGHT = 360;
  const centerX = VIEWBOX_WIDTH / 2;
  const centerY = VIEWBOX_HEIGHT / 2;
  const maxRadius = 140;
  const labelRingRadius = maxRadius + 36;
  const gridLevels = [0.25, 0.5, 0.75, 1];
  
  // Sanitize and normalize data
  const normalizedSeries = useMemo(() => {
    if (!Array.isArray(series)) return [];
    
    return series.map((seriesItem, index) => {
      const data = Array.isArray(seriesItem?.data) ? seriesItem.data : [];
      const sanitizedData = data.map((value, dataIndex) => {
        if (typeof value !== 'number' || Number.isNaN(value)) return 0;
        return Math.min(Math.max(value, 0), max);
      });
      
      return {
        id: seriesItem?.label || `series-${index}`,
        label: seriesItem?.label || `Series ${index + 1}`,
        data: metrics.map((_, dataIndex) => sanitizedData[dataIndex] ?? 0),
        color: palette[index % palette.length],
      };
    });
  }, [series, metrics, max]);

  const wrapMetricLabel = useMemo(() => {
    return (label) => {
      if (!label) return [''];
      const safeLabel = String(label);
      const words = safeLabel.split(' ');
      const lines = [];
      let currentLine = '';

      words.forEach((word) => {
        const tentative = currentLine ? `${currentLine} ${word}` : word;
        if (tentative.length <= 13 || !currentLine) {
          currentLine = tentative;
        } else {
          lines.push(currentLine);
          currentLine = word;
        }
      });

      if (currentLine) lines.push(currentLine);
      return lines.slice(0, 3);
    };
  }, []);

  const labelLayouts = useMemo(() => {
    if (!metrics.length) return [];

    const angleStep = (2 * Math.PI) / metrics.length;
    const startAngle = -Math.PI / 2;
    const padding = 18;

    return metrics.map((metric, index) => {
      const angle = startAngle + index * angleStep;
      const directionX = Math.cos(angle);
      const directionY = Math.sin(angle);

      const lines = wrapMetricLabel(metric);
      const longestLine = lines.reduce((acc, line) => Math.max(acc, line.length), 0);
      const boxWidth = Math.min(Math.max(longestLine * 7.6 + 22, 96), 148);
      const boxHeight = lines.length * 16 + 20;

      const rawX = centerX + labelRingRadius * directionX;
      const rawY = centerY + labelRingRadius * directionY;

      let boxX = rawX - boxWidth / 2;
      let boxY = rawY - boxHeight / 2;

      if (directionX < -0.25) {
        boxX = rawX - boxWidth + 12;
      } else if (directionX > 0.25) {
        boxX = rawX - 12;
      }

      boxX = Math.min(Math.max(boxX, padding), VIEWBOX_WIDTH - boxWidth - padding);
      boxY = Math.min(Math.max(boxY, padding), VIEWBOX_HEIGHT - boxHeight - padding);

      const boxCenterX = boxX + boxWidth / 2;
      const boxCenterY = boxY + boxHeight / 2;

      const connectorInnerX = centerX + (maxRadius + 10) * directionX;
      const connectorInnerY = centerY + (maxRadius + 10) * directionY;

      return {
        metric,
        lines,
        boxX,
        boxY,
        boxWidth,
        boxHeight,
        boxCenterX,
        boxCenterY,
        connectorInnerX,
        connectorInnerY,
      };
    });
  }, [metrics, wrapMetricLabel, labelRingRadius, centerX, centerY, maxRadius]);

  // Empty state
  if (!metrics.length || !normalizedSeries.length) {
    return (
      <Box
        sx={{
          height,
          width: '100%',
          borderRadius: '24px',
          background: 'linear-gradient(135deg, rgba(255,255,255,0.12) 0%, rgba(42,78,112,0.18) 52%, rgba(16,28,44,0.16) 100%)',
          border: '1px dashed rgba(233, 196, 106, 0.24)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'rgba(255,255,255,0.65)',
          fontSize: 15,
          letterSpacing: 0.3,
        }}
      >
        Evaluation metrics will appear here once available.
      </Box>
    );
  }

  const numPoints = metrics.length;
  const angleStep = (2 * Math.PI) / numPoints;
  const startAngle = -Math.PI / 2;

  // Calculate angle for each metric point
  // Start from top

  // Generate grid polygon points
  const getPolygonPoints = (radiusFactor) => {
    return metrics.map((_, index) => {
      const angle = startAngle + index * angleStep;
      const x = centerX + maxRadius * radiusFactor * Math.cos(angle);
      const y = centerY + maxRadius * radiusFactor * Math.sin(angle);
      return `${x},${y}`;
    }).join(' ');
  };

  // Generate data polygon points for each series
  const getDataPolygonPoints = (dataArray) => {
    return dataArray.map((value, index) => {
      const angle = startAngle + index * angleStep;
      const normalizedValue = value / max;
      const radius = maxRadius * normalizedValue;
      const x = centerX + radius * Math.cos(angle);
      const y = centerY + radius * Math.sin(angle);
      return `${x},${y}`;
    }).join(' ');
  };

  return (
    <Box
      sx={{
        width: '100%',
        height,
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '12px',
        '@keyframes pulse': {
          '0%, 100%': {
            opacity: 0.2,
            transform: 'scale(1)',
          },
          '50%': {
            opacity: 0.4,
            transform: 'scale(1.1)',
          },
        },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 12,
          borderRadius: '32px',
          background: 'linear-gradient(135deg, rgba(255,255,255,0.12) 0%, rgba(52,86,130,0.2) 48%, rgba(18,30,48,0.18) 100%)',
          border: '1px solid rgba(233,196,106,0.2)',
          boxShadow: '0 22px 55px rgba(10, 18, 35, 0.45)',
          backdropFilter: 'blur(18px)',
          zIndex: 0,
        }}
      />
      <svg
        width="100%"
        height="100%"
        viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
        style={{ 
          overflow: 'visible', 
          position: 'relative', 
          zIndex: 1,
          maxWidth: '100%',
          maxHeight: '100%'
        }}
      >
        <defs>
          <linearGradient id="labelGlassGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="rgba(255,255,255,0.2)" />
            <stop offset="55%" stopColor="rgba(56,98,140,0.26)" />
            <stop offset="100%" stopColor="rgba(18,30,48,0.22)" />
          </linearGradient>
        </defs>
        {/* Gradient definitions */}
        <defs>
          {normalizedSeries.map((seriesItem, idx) => (
            <React.Fragment key={`gradients-${idx}`}>
              {/* Radial gradient for filled area */}
              <radialGradient
                id={`gradient-${idx}`}
                cx="50%"
                cy="50%"
                r="50%"
              >
                <stop offset="0%" stopColor={seriesItem.color} stopOpacity="0.45" />
                <stop offset="50%" stopColor={seriesItem.color} stopOpacity="0.25" />
                <stop offset="100%" stopColor={seriesItem.color} stopOpacity="0.08" />
              </radialGradient>
              
              {/* Glow filter for enhanced effect */}
              <filter id={`glow-${idx}`}>
                <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                <feMerge>
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </React.Fragment>
          ))}
        </defs>

        {/* Concentric grid circles */}
        {gridLevels.map((level, idx) => (
          <polygon
            key={`grid-${idx}`}
            points={getPolygonPoints(level)}
            fill="none"
            stroke="rgba(233, 196, 106, 0.12)"
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
        ))}

        {/* Radial lines from center to vertices */}
        {metrics.map((_, index) => {
          const angle = startAngle + index * angleStep;
          const endX = centerX + maxRadius * Math.cos(angle);
          const endY = centerY + maxRadius * Math.sin(angle);
          return (
            <line
              key={`radial-${index}`}
              x1={centerX}
              y1={centerY}
              x2={endX}
              y2={endY}
              stroke="rgba(244, 162, 97, 0.15)"
              strokeWidth="1.5"
            />
          );
        })}

        {/* Data polygons (series) */}
        {normalizedSeries.map((seriesItem, idx) => (
          <g key={`series-${idx}`}>
            {/* Filled area with enhanced visuals */}
            <polygon
              points={getDataPolygonPoints(seriesItem.data)}
              fill={`url(#gradient-${idx})`}
              stroke={seriesItem.color}
              strokeWidth="2.5"
              strokeLinejoin="round"
              strokeLinecap="round"
              style={{
                filter: `drop-shadow(0 4px 12px ${seriesItem.color}44) drop-shadow(0 0 8px ${seriesItem.color}22)`,
                transition: 'all 0.5s ease',
              }}
            />
            
            {/* Subtle inner glow polygon */}
            <polygon
              points={getDataPolygonPoints(seriesItem.data)}
              fill="none"
              stroke={seriesItem.color}
              strokeWidth="1"
              strokeLinejoin="round"
              opacity="0.4"
              style={{
                filter: `blur(2px)`,
              }}
            />
            
            {/* Data points with hover effects */}
            {seriesItem.data.map((value, pointIdx) => {
              const angle = startAngle + pointIdx * angleStep;
              const normalizedValue = value / max;
              const radius = maxRadius * normalizedValue;
              const x = centerX + radius * Math.cos(angle);
              const y = centerY + radius * Math.sin(angle);
              const isHovered = hoveredPoint?.seriesIdx === idx && hoveredPoint?.pointIdx === pointIdx;
              
              return (
                <g 
                  key={`point-${idx}-${pointIdx}`}
                  onMouseEnter={() => setHoveredPoint({ 
                    seriesIdx: idx, 
                    pointIdx, 
                    x, 
                    y, 
                    value, 
                    metric: metrics[pointIdx],
                    seriesLabel: seriesItem.label,
                    color: seriesItem.color
                  })}
                  onMouseLeave={() => setHoveredPoint(null)}
                  style={{ cursor: 'pointer', transition: 'all 0.3s ease' }}
                >
                  {/* Hover glow effect */}
                  {isHovered && (
                    <circle
                      cx={x}
                      cy={y}
                      r="14"
                      fill={seriesItem.color}
                      opacity="0.2"
                      style={{
                        animation: 'pulse 1.5s ease-in-out infinite',
                      }}
                    />
                  )}
                  
                  {/* Outer circle */}
                  <circle
                    cx={x}
                    cy={y}
                    r={isHovered ? "8" : "6"}
                    fill="#0b0d1d"
                    stroke={seriesItem.color}
                    strokeWidth={isHovered ? "3" : "2.5"}
                    style={{
                      transition: 'all 0.3s ease',
                      filter: isHovered ? `drop-shadow(0 0 8px ${seriesItem.color})` : 'none'
                    }}
                  />
                  
                  {/* Inner circle */}
                  <circle
                    cx={x}
                    cy={y}
                    r={isHovered ? "4" : "3"}
                    fill={seriesItem.color}
                    opacity={isHovered ? "1" : "0.8"}
                    style={{ transition: 'all 0.3s ease' }}
                  />
                  
                  {/* Invisible larger hit area for better UX */}
                  <circle
                    cx={x}
                    cy={y}
                    r="16"
                    fill="transparent"
                    style={{ pointerEvents: 'all' }}
                  />
                </g>
              );
            })}
          </g>
        ))}

        {/* Label callouts */}
        {labelLayouts.map((layout, index) => (
          <g key={`label-${index}`}>
            <line
              x1={layout.connectorInnerX}
              y1={layout.connectorInnerY}
              x2={layout.boxCenterX}
              y2={layout.boxCenterY}
              stroke="rgba(233,196,106,0.28)"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
            <rect
              x={layout.boxX}
              y={layout.boxY}
              width={layout.boxWidth}
              height={layout.boxHeight}
              rx="14"
              ry="14"
              fill="url(#labelGlassGradient)"
              stroke="rgba(233,196,106,0.24)"
              strokeWidth="1"
            />
            {layout.lines.map((line, lineIdx) => (
              <text
                key={`label-line-${index}-${lineIdx}`}
                x={layout.boxCenterX}
                y={layout.boxY + 16 + lineIdx * 16}
                textAnchor="middle"
                fill="rgba(255,255,255,0.88)"
                fontSize="12"
                fontWeight="600"
                letterSpacing="0.02em"
                style={{ textTransform: 'capitalize' }}
              >
                {line}
              </text>
            ))}
          </g>
        ))}

        {/* Center point */}
        <circle
          cx={centerX}
          cy={centerY}
          r="4"
          fill="rgba(233, 196, 106, 0.8)"
        />
      </svg>

      {/* Glassmorphism overlay gradient */}
      <Box
        sx={{
          pointerEvents: 'none',
          position: 'absolute',
          inset: 12,
          borderRadius: '32px',
          background:
            'radial-gradient(circle at 50% 35%, rgba(233,196,106,0.12) 0%, transparent 58%)',
          zIndex: 2,
        }}
      />

      {badgeLabel && (
        <Box
          sx={{
            pointerEvents: 'none',
            position: 'absolute',
            top: 18,
            right: 24,
            borderRadius: '999px',
            padding: '6px 16px',
            background: 'rgba(20,20,35,0.65)',
            border: '1px solid rgba(233,196,106,0.26)',
            color: 'rgba(233,196,106,0.95)',
            fontSize: '12px',
            fontWeight: 600,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            zIndex: 3,
          }}
        >
          {badgeLabel}
        </Box>
      )}

      {/* Elegant Tooltip on Hover */}
      {hoveredPoint && (
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none',
            zIndex: 10,
            animation: 'fadeIn 0.3s ease',
            '@keyframes fadeIn': {
              from: { opacity: 0, transform: 'translate(-50%, -50%) scale(0.95)' },
              to: { opacity: 1, transform: 'translate(-50%, -50%) scale(1)' }
            }
          }}
        >
          <Box
            sx={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.16) 0%, rgba(52,86,130,0.24) 55%, rgba(18,30,48,0.2) 100%)',
              backdropFilter: 'blur(24px)',
              borderRadius: '20px',
              padding: '20px 28px',
              border: `2px solid ${hoveredPoint.color}`,
              boxShadow: `0 12px 36px rgba(10,18,35,0.45), 0 0 0 1px ${hoveredPoint.color}33, 0 0 30px ${hoveredPoint.color}3a`,
              minWidth: '200px',
              textAlign: 'center',
            }}
          >
            {/* Metric Name */}
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.7)',
                fontSize: '11px',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                mb: 1,
              }}
            >
              {hoveredPoint.metric}
            </Typography>
            
            {/* Score Display */}
            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 0.5, mb: 1 }}>
              <Typography
                sx={{
                  color: hoveredPoint.color,
                  fontSize: '36px',
                  fontWeight: 700,
                  lineHeight: 1,
                  textShadow: `0 0 20px ${hoveredPoint.color}66`,
                }}
              >
                {hoveredPoint.value.toFixed(1)}
              </Typography>
              <Typography
                sx={{
                  color: 'rgba(255,255,255,0.5)',
                  fontSize: '18px',
                  fontWeight: 500,
                  mb: 0.5,
                }}
              >
                / {max}
              </Typography>
            </Box>
            
            {/* Percentage Bar */}
            <Box sx={{ mb: 1.5 }}>
              <Box
                sx={{
                  height: '6px',
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: '3px',
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                <Box
                  sx={{
                    height: '100%',
                    width: `${(hoveredPoint.value / max) * 100}%`,
                    background: `linear-gradient(90deg, ${hoveredPoint.color} 0%, ${hoveredPoint.color}dd 100%)`,
                    borderRadius: '3px',
                    boxShadow: `0 0 10px ${hoveredPoint.color}88`,
                    transition: 'width 0.5s ease',
                  }}
                />
              </Box>
            </Box>
            
            {/* Percentage Text */}
            <Typography
              sx={{
                color: hoveredPoint.color,
                fontSize: '13px',
                fontWeight: 600,
                letterSpacing: '0.05em',
              }}
            >
              {((hoveredPoint.value / max) * 100).toFixed(0)}% Performance
            </Typography>
            
            {/* Series Label if multiple series */}
            {normalizedSeries.length > 1 && (
              <Typography
                sx={{
                  color: 'rgba(255,255,255,0.5)',
                  fontSize: '10px',
                  fontWeight: 500,
                  mt: 1,
                  letterSpacing: '0.05em',
                }}
              >
                {hoveredPoint.seriesLabel}
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {/* Legend */}
      {normalizedSeries.length > 1 && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 8,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: 2,
            background: 'rgba(10, 12, 22, 0.75)',
            backdropFilter: 'blur(12px)',
            borderRadius: '16px',
            padding: '8px 16px',
            border: '1px solid rgba(233, 196, 106, 0.2)',
            zIndex: 3,
          }}
        >
          {normalizedSeries.map((seriesItem, idx) => (
            <Box
              key={`legend-${idx}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  bgcolor: seriesItem.color,
                  boxShadow: `0 0 8px ${seriesItem.color}66`,
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255,255,255,0.85)',
                  fontWeight: 600,
                  fontSize: '11px',
                  letterSpacing: '0.03em',
                }}
              >
                {seriesItem.label}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default SkillsRadarChart;
